import { gql, useMutation, useQuery } from "@apollo/client";
import { JsonForms } from "@jsonforms/react";
import { Card, Checkbox, Form, FormLayout, Layout, Page, Select, SkeletonBodyText, SkeletonDisplayText, SkeletonPage, TextField } from "@shopify/polaris";
import { asChoiceField, useField, useForm } from "@shopify/react-form";
import { Helmet } from "react-helmet";
import { useHistory, useParams } from "react-router";
import { ENUM_LABEL_VALUE } from "../utils/ENUM_LABEL_VALUE.query";
import { polarisRenderers, vanillaCells } from "../utils/MetaModal";

const SHIPPER_FRAGMENT = gql`fragment ShipperFragment on Shipper{id name active group config}`
const SHIPPER_QUERY = gql`${SHIPPER_FRAGMENT}query($id:ID!){shipper(id:$id){...ShipperFragment}}`
const SHIPPER_MUTATION = gql`${SHIPPER_FRAGMENT}mutation($shipper:ShipperInput!,$id:ID){shipper(shipper:$shipper,id:$id){...ShipperFragment}}`

const SHIPPER_CONFIGURATIONS = gql(`{shipperConfigurations}`)

export default function Shipper() {
    const { id } = useParams();
    const history = useHistory();
    const { data: { shipper } = { shipper: {} }, loading } = useQuery(SHIPPER_QUERY, { variables: { id } })
    const { data: { __type: { enumValues: shipperGroups }, loading: shipperGroupsLoading } = { __type: { enumValues: [] } } } = useQuery(ENUM_LABEL_VALUE, { variables: { type: 'ShipperGroup' } })
    const { data: { shipperConfigurations } = { paymentProviderConfigurations: {} } } = useQuery(SHIPPER_CONFIGURATIONS)
    const title = shipper?.name ?? 'New shipper'
    const [mutate, { loading: mutating }] = useMutation(SHIPPER_MUTATION)
    const {
        fields,
        submit
    } = useForm({
        fields: {
            name: useField(shipper?.name),
            active: useField(shipper?.active),
            group: useField(shipper?.group || shipperGroups[0]?.value),
            config: useField(shipper?.config || {}),
        },
        onSubmit(shipper) {
            return mutate({
                variables: {
                    id,
                    shipper
                }
            }).then(
                response => {
                    history.replace("/settings/shippers/" + response.data.shipper.id);
                    return { status: 'success' }
                }
            )
        }
    })

    if (loading || shipperGroupsLoading) {
        return (
            <SkeletonPage primaryAction>
                <Helmet>
                    <title>Loading...</title>
                </Helmet>
                <Layout>
                    <Layout.AnnotatedSection
                        title={<SkeletonDisplayText size="medium" />}
                        description={<SkeletonBodyText />}
                    >
                        <Card sectioned>
                            <SkeletonBodyText />
                        </Card>
                    </Layout.AnnotatedSection>
                </Layout>
                <Layout>
                    <Layout.AnnotatedSection
                        title={<SkeletonDisplayText size="medium" />}
                        description={<SkeletonBodyText />}
                    >
                        <Card sectioned>
                            <SkeletonBodyText />
                        </Card>
                    </Layout.AnnotatedSection>
                </Layout>
            </SkeletonPage>
        )
    }

    return (
        <Page title={title} primaryAction={{ content: 'Save', onAction: submit, loading: mutating, disabled: loading }} breadcrumbs={[{ content: 'Shippers', url: '/settings/shippers' }]}>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <Form onSubmit={submit}>
                <Layout>
                    <Layout.AnnotatedSection
                        title="Details"
                        description="Give this shipper a short name to make it easy to identify. You’ll see this name in areas like orders and products."
                    >
                        <Card sectioned>
                            <FormLayout>
                                <TextField label="Shipper Name" {...fields.name} />
                                <Checkbox label="Active" {...asChoiceField(fields.active)} />
                                <Select
                                    label="Group"
                                    options={shipperGroups}
                                    {...fields.group} />
                            </FormLayout>
                        </Card>
                    </Layout.AnnotatedSection>
                    {shipperConfigurations && fields.group.value && shipperConfigurations[fields.group.value] && <Layout.AnnotatedSection
                        title="Configuration"
                    >
                        <Card sectioned>
                            <JsonForms
                                schema={shipperConfigurations[fields.group.value].control}
                                uischema={shipperConfigurations[fields.group.value].ui}
                                data={fields.config.value}
                                onChange={({ errors, data }) => fields.config.onChange(data)}
                                renderers={polarisRenderers}
                                cells={vanillaCells}
                            />
                        </Card>
                    </Layout.AnnotatedSection>}
                </Layout>
            </Form>
        </Page>
    )
}